import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth, db } from '../firebase';
import {
  signInWithEmailAndPassword, signInWithPopup,
  GoogleAuthProvider, signOut, onAuthStateChanged
} from 'firebase/auth';
import { Link } from 'react-router-dom';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc,
  doc, orderBy, query, serverTimestamp
} from 'firebase/firestore';

const googleProvider = new GoogleAuthProvider();
const CLOUDINARY_CLOUD = 'dulpswa1m';
const CLOUDINARY_PRESET = 'Khisa-media';

const EMPTY_EVENT = { title: '', date: '', time: '', location: '', description: '', imageUrl: '' };

// ─── Helpers ─────────────────────────────────────────────────────────────────

const compressImage = (file) =>
  new Promise((resolve) => {
    if (!file.type.startsWith('image/')) { resolve(file); return; }
    const img = new Image();
    img.onload = () => {
      const MAX = 1600;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) { height = Math.round((height * MAX) / width); width = MAX; }
        else { width = Math.round((width * MAX) / height); height = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name, { type: 'image/jpeg' })),
        'image/jpeg', 0.82
      );
    };
    img.src = URL.createObjectURL(file);
  });

const uploadFile = (file, onProgress) =>
  new Promise(async (resolve, reject) => {
    const compressed = await compressImage(file);
    const formData = new FormData();
    formData.append('file', compressed);
    formData.append('upload_preset', CLOUDINARY_PRESET);
    formData.append('folder', 'media');
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status === 200) resolve(data);
        else reject(new Error(data.error?.message || 'Upload failed'));
      } catch { reject(new Error('Invalid response from Cloudinary')); }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/auto/upload`);
    xhr.send(formData);
  });

// ─── Small components ─────────────────────────────────────────────────────────

const EyeIcon = ({ open }) => open ? (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
) : (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className={`fixed top-5 right-5 z-50 flex items-center gap-2 px-5 py-3 rounded-xl shadow-xl text-white text-sm font-medium ${
      toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
    }`}>
      {toast.type === 'success'
        ? <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
        : <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
      }
      {toast.message}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Admin() {
  // Auth
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  // Tabs
  const [activeTab, setActiveTab] = useState('media');

  // Media
  const [media, setMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  // Events
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null); // null = creating new
  const [evtForm, setEvtForm] = useState(EMPTY_EVENT);
  const [evtImageFile, setEvtImageFile] = useState(null);
  const [evtImagePreview, setEvtImagePreview] = useState('');
  const [evtSaving, setEvtSaving] = useState(false);
  const [evtImageProgress, setEvtImageProgress] = useState(0);
  const [evtError, setEvtError] = useState('');
  const evtImageRef = useRef();

  // Toast
  const [toast, setToast] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ── Auth ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => { setUser(u); setAuthLoading(false); });
    return unsub;
  }, []);

  useEffect(() => {
    if (user) { loadMedia(); loadEvents(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Media ─────────────────────────────────────────────────────────────────

  const loadMedia = async () => {
    setMediaLoading(true);
    try {
      const res = await fetch('/api/gallery');
      const data = await res.json();
      setMedia(Array.isArray(data) ? data : []);
    } catch {
      showToast('error', 'Failed to load media');
    } finally {
      setMediaLoading(false);
    }
  };

  const addFiles = useCallback((files) => {
    const next = Array.from(files).map((file) => ({
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      progress: 0,
      status: 'pending',
    }));
    setSelectedFiles((prev) => [...prev, ...next]);
  }, []);

  const removeSelected = (id) => {
    setSelectedFiles((prev) => {
      const f = prev.find((x) => x.id === id);
      if (f?.preview) URL.revokeObjectURL(f.preview);
      return prev.filter((x) => x.id !== id);
    });
  };

  const updateFile = (id, updates) =>
    setSelectedFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)));

  const handleUpload = async () => {
    const pending = selectedFiles.filter((f) => f.status === 'pending');
    if (!pending.length) return;
    setUploading(true);

    const results = await Promise.allSettled(
      pending.map(({ id, file }) => {
        updateFile(id, { status: 'uploading' });
        return uploadFile(file, (pct) => updateFile(id, { progress: pct }))
          .then((data) => {
            updateFile(id, { status: 'done', progress: 100 });
            return {
              id: data.public_id,
              filePath: data.secure_url,
              type: file.type.startsWith('video') ? 'video' : 'image',
              title: file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '),
              createdAt: data.created_at,
            };
          })
          .catch((err) => { updateFile(id, { status: 'error' }); throw err; });
      })
    );

    const succeeded = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
    const failed = results.filter((r) => r.status === 'rejected').length;

    if (succeeded.length) {
      setMedia((prev) => [...succeeded, ...prev]);
      showToast('success', `${succeeded.length} file${succeeded.length > 1 ? 's' : ''} uploaded`);
      setTimeout(() => setSelectedFiles((prev) => prev.filter((f) => f.status !== 'done')), 1500);
    }
    if (failed) showToast('error', `${failed} file${failed > 1 ? 's' : ''} failed`);
    setUploading(false);
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    setMedia((prev) => prev.filter((m) => m.id !== item.id));
    try {
      await fetch(`/api/delete?publicId=${encodeURIComponent(item.id)}&resourceType=${item.type}`, { method: 'DELETE' });
      showToast('success', 'Item deleted');
    } catch {
      setMedia((prev) => [item, ...prev]);
      showToast('error', 'Failed to delete item');
    }
  };

  // ── Events ────────────────────────────────────────────────────────────────

  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const q = query(collection(db, 'events'), orderBy('date', 'asc'));
      const snap = await getDocs(q);
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch {
      showToast('error', 'Failed to load events');
    } finally {
      setEventsLoading(false);
    }
  };

  const startNewEvent = () => {
    setEditingEvent(null);
    setEvtForm(EMPTY_EVENT);
    setEvtImageFile(null);
    setEvtImagePreview('');
    setEvtImageProgress(0);
    setEvtError('');
  };

  const startEditEvent = (event) => {
    setEditingEvent(event);
    setEvtForm({
      title: event.title || '',
      date: event.date || '',
      time: event.time || '',
      location: event.location || '',
      description: event.description || '',
      imageUrl: event.imageUrl || '',
    });
    setEvtImageFile(null);
    setEvtImagePreview(event.imageUrl || '');
    setEvtImageProgress(0);
    setEvtError('');
  };

  const handleEvtImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEvtImageFile(file);
    setEvtImagePreview(URL.createObjectURL(file));
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    setEvtError('');
    if (!evtForm.title || !evtForm.date) {
      setEvtError('Title and date are required');
      return;
    }
    setEvtSaving(true);
    try {
      let imageUrl = evtForm.imageUrl;

      if (evtImageFile) {
        setEvtImageProgress(1);
        const data = await uploadFile(evtImageFile, setEvtImageProgress);
        imageUrl = data.secure_url;
        setEvtImageProgress(100);
      }

      const payload = {
        title: evtForm.title.trim(),
        date: evtForm.date,
        time: evtForm.time.trim(),
        location: evtForm.location.trim(),
        description: evtForm.description.trim(),
        imageUrl,
      };

      if (editingEvent) {
        await updateDoc(doc(db, 'events', editingEvent.id), payload);
        setEvents((prev) => prev.map((ev) => ev.id === editingEvent.id ? { ...ev, ...payload } : ev));
        showToast('success', 'Event updated');
      } else {
        const ref = await addDoc(collection(db, 'events'), { ...payload, createdAt: serverTimestamp() });
        setEvents((prev) => [...prev, { id: ref.id, ...payload }].sort((a, b) => a.date.localeCompare(b.date)));
        showToast('success', 'Event created');
      }

      startNewEvent();
    } catch (err) {
      console.error('[Admin] Save event error:', err);
      const msg = err?.message || 'Failed to save event';
      setEvtError(msg);
      showToast('error', msg);
    } finally {
      setEvtSaving(false);
      setEvtImageProgress(0);
    }
  };

  const handleDeleteEvent = async (event) => {
    if (!window.confirm(`Delete "${event.title}"? This cannot be undone.`)) return;
    setEvents((prev) => prev.filter((e) => e.id !== event.id));
    try {
      await deleteDoc(doc(db, 'events', event.id));
      showToast('success', 'Event deleted');
      if (editingEvent?.id === event.id) startNewEvent();
    } catch {
      setEvents((prev) => [...prev, event].sort((a, b) => a.date.localeCompare(b.date)));
      showToast('error', 'Failed to delete event');
    }
  };

  // ── Login ─────────────────────────────────────────────────────────────────

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setSigningIn(true);
    try { await signInWithEmailAndPassword(auth, email, password); }
    catch { setLoginError('Incorrect email or password. Please try again.'); }
    finally { setSigningIn(false); }
  };

  const handleGoogleLogin = async () => {
    setLoginError('');
    setSigningIn(true);
    try { await signInWithPopup(auth, googleProvider); }
    catch (err) {
      if (err.code !== 'auth/popup-closed-by-user')
        setLoginError('Google sign-in failed. Please try again.');
    }
    finally { setSigningIn(false); }
  };

  // ── Render: loading / login ───────────────────────────────────────────────

  if (authLoading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <svg className="animate-spin h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
    </div>
  );

  if (!user) return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-red-600 flex-col items-center justify-center px-12 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="relative z-10 flex flex-col items-center text-center max-w-sm">
          <img src="/assets/logo.jpg" alt="Logo" className="h-24 w-24 rounded-full object-cover shadow-xl border-4 border-white/30 mb-6" />
          <h1 className="text-3xl font-bold leading-tight mb-3">Khisa Foundation for Impact and Empowerment</h1>
          <p className="text-red-100 text-sm leading-relaxed">Admin portal — manage media and events shown to the public.</p>
          <div className="mt-8 flex gap-2">
            <span className="h-2 w-8 bg-white/60 rounded-full" />
            <span className="h-2 w-2 bg-white/30 rounded-full" />
            <span className="h-2 w-2 bg-white/30 rounded-full" />
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 px-6 py-12">
        <div className="flex flex-col items-center mb-8 lg:hidden">
          <img src="/assets/logo.jpg" alt="Logo" className="h-16 w-16 rounded-full object-cover shadow-md border-4 border-red-600 mb-3" />
          <h1 className="text-xl font-bold text-gray-800 text-center">Khisa Foundation</h1>
        </div>
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
            <p className="text-gray-500 text-sm mt-1">Sign in to manage media and events</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email address</label>
              <input type="email" placeholder="admin@khisafoundation.org" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                required autoComplete="email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                  required autoComplete="current-password" />
                <button type="button" onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                  <EyeIcon open={showPassword} />
                </button>
              </div>
            </div>
            {loginError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <svg className="h-4 w-4 text-red-500 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-red-600 text-sm">{loginError}</p>
              </div>
            )}
            <button type="submit" disabled={signingIn}
              className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2">
              {signingIn
                ? <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>Signing in...</>
                : 'Sign In'}
            </button>
          </form>
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-gray-200" /><span className="text-xs text-gray-400">or</span><div className="flex-1 h-px bg-gray-200" />
          </div>
          <button type="button" onClick={handleGoogleLogin} disabled={signingIn}
            className="w-full flex items-center justify-center gap-3 border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-60 text-gray-700 font-medium py-3 rounded-lg transition-colors">
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
          <p className="mt-6 text-center text-xs text-gray-400">Khisa Foundation Admin Portal &mdash; restricted access</p>
        </div>
      </div>
    </div>
  );

  // ── Render: dashboard ─────────────────────────────────────────────────────

  const pendingCount = selectedFiles.filter((f) => f.status === 'pending').length;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toast toast={toast} />

      {/* Header */}
      <div className="bg-red-600 text-white px-6 py-3 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
          <img src="/assets/logo.jpg" alt="Logo" className="h-9 w-9 rounded-full object-cover border-2 border-white/30" />
          <span className="font-bold">Khisa Foundation Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-red-200 text-sm hidden sm:block">{user.email}</span>
          <Link to="/"
            className="text-sm bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors font-medium flex items-center gap-1.5">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Website
          </Link>
          <button onClick={() => signOut(auth)}
            className="text-sm bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors font-medium">
            Sign Out
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="max-w-7xl mx-auto flex gap-1">
          {[
            { key: 'media', label: 'Media' },
            { key: 'events', label: 'Events' },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* ── MEDIA TAB ─────────────────────────────────────────────────── */}
        {activeTab === 'media' && (
          <>
            {/* Upload Zone */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 pt-5 pb-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-800">Upload Media</h2>
              </div>
              <div className="p-6">
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setIsDragging(false); addFiles(e.dataTransfer.files); }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl py-10 flex flex-col items-center gap-3 cursor-pointer transition-colors ${
                    isDragging ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-red-300 hover:bg-gray-50'
                  }`}
                >
                  <svg className={`h-12 w-12 transition-colors ${isDragging ? 'text-red-400' : 'text-gray-300'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600">{isDragging ? 'Drop to upload' : 'Drag & drop files here'}</p>
                    <p className="text-xs text-gray-400 mt-1">or <span className="text-red-500 font-medium">click to browse</span> · Images & videos · Multiple files</p>
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple className="hidden"
                    onChange={(e) => addFiles(e.target.files)} />
                </div>

                {selectedFiles.length > 0 && (
                  <div className="mt-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-600">
                        {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                      </span>
                      {!uploading && (
                        <button onClick={() => setSelectedFiles([])} className="text-xs text-gray-400 hover:text-red-500 transition-colors">
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                      {selectedFiles.map(({ id, file, preview, progress, status }) => (
                        <div key={id} className="relative rounded-lg overflow-hidden bg-gray-100 aspect-square">
                          {preview
                            ? <img src={preview} alt={file.name} className="w-full h-full object-cover" />
                            : <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                                <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </div>
                          }
                          {status === 'uploading' && (
                            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-1">
                              <span className="text-white text-xs font-bold">{progress}%</span>
                              <div className="w-4/5 h-1 bg-white/30 rounded">
                                <div className="h-full bg-white rounded transition-all duration-150" style={{ width: `${progress}%` }} />
                              </div>
                            </div>
                          )}
                          {status === 'done' && (
                            <div className="absolute inset-0 bg-green-500/70 flex items-center justify-center">
                              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                          {status === 'error' && (
                            <div className="absolute inset-0 bg-red-500/70 flex items-center justify-center">
                              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </div>
                          )}
                          {status === 'pending' && (
                            <button onClick={() => removeSelected(id)}
                              className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs leading-none transition-colors">
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button onClick={handleUpload} disabled={uploading || pendingCount === 0}
                      className="mt-4 w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
                      {uploading
                        ? <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>Uploading in parallel...</>
                        : `Upload ${pendingCount} file${pendingCount !== 1 ? 's' : ''}`
                      }
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Media Library */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-semibold text-gray-800">
                  Media Library <span className="text-gray-400 font-normal text-sm ml-1">({media.length})</span>
                </h2>
                <button onClick={loadMedia} disabled={mediaLoading}
                  className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50 flex items-center gap-1">
                  {mediaLoading && <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>}
                  Refresh
                </button>
              </div>
              <div className="p-6">
                {mediaLoading ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {Array.from({ length: 12 }).map((_, i) => (
                      <div key={i} className="rounded-xl overflow-hidden animate-pulse">
                        <div className="bg-gray-200 aspect-square" />
                      </div>
                    ))}
                  </div>
                ) : media.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <svg className="h-12 w-12 mx-auto mb-3 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm">No media yet. Upload some files above.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {media.map((item) => (
                      <div key={item.id} className="rounded-xl overflow-hidden border border-gray-100 group relative bg-gray-50">
                        {item.type === 'image'
                          ? <img src={item.filePath} alt={item.title} className="w-full aspect-square object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }} loading="lazy" />
                          : <video src={item.filePath} className="w-full aspect-square object-cover bg-gray-800" />
                        }
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button onClick={() => handleDelete(item)}
                            className="bg-red-600 hover:bg-red-700 text-white text-xs px-4 py-2 rounded-lg font-medium transition-colors">
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── EVENTS TAB ────────────────────────────────────────────────── */}
        {activeTab === 'events' && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">

            {/* Form */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-semibold text-gray-800">
                  {editingEvent ? 'Edit Event' : 'New Event'}
                </h2>
                {editingEvent && (
                  <button onClick={startNewEvent} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                    + New event
                  </button>
                )}
              </div>
              <form onSubmit={handleSaveEvent} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Title <span className="text-red-500">*</span></label>
                  <input type="text" placeholder="Event title" value={evtForm.title}
                    onChange={(e) => setEvtForm((f) => ({ ...f, title: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Date <span className="text-red-500">*</span></label>
                    <input type="date" value={evtForm.date}
                      onChange={(e) => setEvtForm((f) => ({ ...f, date: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
                    <input type="time" value={evtForm.time}
                      onChange={(e) => setEvtForm((f) => ({ ...f, time: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
                  <input type="text" placeholder="e.g. Nairobi, Kenya" value={evtForm.location}
                    onChange={(e) => setEvtForm((f) => ({ ...f, location: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent" />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                  <textarea placeholder="Brief description of the event..." value={evtForm.description}
                    onChange={(e) => setEvtForm((f) => ({ ...f, description: e.target.value }))}
                    rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none" />
                </div>

                {/* Cover image */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Cover Image (optional)</label>
                  {evtImagePreview ? (
                    <div className="relative rounded-lg overflow-hidden">
                      <img src={evtImagePreview} alt="preview" className="w-full h-36 object-cover" />
                      <button type="button"
                        onClick={() => { setEvtImageFile(null); setEvtImagePreview(''); setEvtForm((f) => ({ ...f, imageUrl: '' })); }}
                        className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm transition-colors">
                        ×
                      </button>
                      {evtImageProgress > 0 && evtImageProgress < 100 && (
                        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/20">
                          <div className="h-full bg-white transition-all duration-150" style={{ width: `${evtImageProgress}%` }} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div onClick={() => evtImageRef.current?.click()}
                      className="border-2 border-dashed border-gray-200 hover:border-red-300 rounded-lg py-6 flex flex-col items-center gap-1 cursor-pointer transition-colors">
                      <svg className="h-7 w-7 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-xs text-gray-400">Click to add a cover image</span>
                    </div>
                  )}
                  <input ref={evtImageRef} type="file" accept="image/*" className="hidden" onChange={handleEvtImageChange} />
                </div>

                {evtError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600">
                    {evtError}
                  </div>
                )}

                <button type="submit" disabled={evtSaving}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm">
                  {evtSaving
                    ? <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>Saving...</>
                    : editingEvent ? 'Update Event' : 'Create Event'
                  }
                </button>
              </form>
            </div>

            {/* Events list */}
            <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-semibold text-gray-800">
                  All Events <span className="text-gray-400 font-normal text-sm ml-1">({events.length})</span>
                </h2>
                <button onClick={loadEvents} disabled={eventsLoading}
                  className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50 flex items-center gap-1">
                  {eventsLoading && <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>}
                  Refresh
                </button>
              </div>

              {eventsLoading ? (
                <div className="p-6 space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <svg className="h-10 w-10 mx-auto mb-3 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">No events yet. Create one using the form.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {events.map((event) => {
                    const isPast = event.date < today;
                    const isEditing = editingEvent?.id === event.id;
                    return (
                      <div key={event.id} className={`flex items-start gap-4 px-6 py-4 transition-colors ${isEditing ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                        {/* Date badge */}
                        <div className={`flex-shrink-0 rounded-xl flex flex-col items-center justify-center w-12 h-12 text-center ${isPast ? 'bg-gray-100' : 'bg-red-600'}`}>
                          <span className={`text-sm font-bold leading-none ${isPast ? 'text-gray-500' : 'text-white'}`}>
                            {new Date(event.date + 'T00:00:00').getDate()}
                          </span>
                          <span className={`text-[10px] uppercase font-semibold ${isPast ? 'text-gray-400' : 'text-red-200'}`}>
                            {new Date(event.date + 'T00:00:00').toLocaleString('default', { month: 'short' })}
                          </span>
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{event.title}</p>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                            {event.location && <span className="text-xs text-gray-400">{event.location}</span>}
                            {event.time && <span className="text-xs text-gray-400">{event.time}</span>}
                          </div>
                          {isPast && <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Past</span>}
                        </div>
                        {/* Actions */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button onClick={() => startEditEvent(event)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded transition-colors">
                            Edit
                          </button>
                          <button onClick={() => handleDeleteEvent(event)}
                            className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded transition-colors">
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
